import { inject, Injectable } from '@angular/core';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { BaseService } from '../../core/common/services/base.abstract.service';
import { ApiFragment } from '../../core/records/entities/api-fragment.class';
import { ActionItem } from '../../elements/components/action-item/entities/action-item.entity';
import { MediaHavenPlugin } from '../entities/mediahaven-plugins.entity';
import { ShPluginActionProvider } from '../entities/plugin-providers.entity';
import { PluginGuard } from '../guards/plugin.guard';

class NoActionItem implements ShPluginActionProvider {
    getAction(record: ApiFragment, plugin: MediaHavenPlugin): Observable<ActionItem | null> {
        return of(null);
    }

    getBulkAction(record: ApiFragment, plugin: MediaHavenPlugin): Observable<ActionItem | null> {
        return of(null);
    }

    getBatchAction(plugin: MediaHavenPlugin): Observable<ActionItem | null> {
        return of(null);
    }
}

@Injectable({ providedIn: 'root' })
export class ShPluginActionsService extends BaseService {
    private readonly pluginGuard: PluginGuard = inject(PluginGuard);

    private readonly PLUGIN_SERVICE_MAPPING: Record<MediaHavenPlugin, ShPluginActionProvider> = {
        [MediaHavenPlugin.ADVANCED_MEDIA_PLAYER]: new NoActionItem(),
        [MediaHavenPlugin.DOSSIER_TRANSFER]: new NoActionItem(),
        [MediaHavenPlugin.FULL_TEXT_SEARCH]: new NoActionItem(),
        [MediaHavenPlugin.METABASE]: new NoActionItem(),
        [MediaHavenPlugin.RESUMABLE_UPLOADS_MH]: new NoActionItem(),
        [MediaHavenPlugin.SEMANTIC_SEARCH]: new NoActionItem(),
        [MediaHavenPlugin.SIMILARITY_SEARCH]: new NoActionItem(),
        [MediaHavenPlugin.VECTOR_FIELD]: new NoActionItem(),
        [MediaHavenPlugin.GOVERNANCE_ARCHIVE_OBJECTS]: new NoActionItem(),
        [MediaHavenPlugin.GENERATIVE_METADATA_MISTRAL_AI]: new NoActionItem(),
        [MediaHavenPlugin.GENERATIVE_METADATA_OPEN_AI]: new NoActionItem(),
        [MediaHavenPlugin.FACIAL_RECOGNITION_DEEP_FACE]: new NoActionItem(),
        [MediaHavenPlugin.VERSION_MANAGEMENT]: new NoActionItem()
    };

    public getPluginActions(record: ApiFragment): Observable<ActionItem[]> {
        return combineLatest(
            Object.keys(MediaHavenPlugin).map(plugin => this.getPluginAction(record, plugin as MediaHavenPlugin))
        ).pipe(map(list => list.filter((x): x is ActionItem => x !== null)));
    }

    private getPluginAction(record: ApiFragment, plugin: MediaHavenPlugin): Observable<ActionItem | null> {
        const actionService: ShPluginActionProvider = this.PLUGIN_SERVICE_MAPPING[plugin];

        return this.checkPluginActive(plugin).pipe(
            switchMap(isActive => (isActive ? actionService.getAction(record, plugin) : of(null)))
        );
    }

    public getPluginBulkActions(records: ApiFragment): Observable<ActionItem[]> {
        return combineLatest(
            Object.keys(MediaHavenPlugin).map(plugin => this.getPluginBulkAction(records, plugin as MediaHavenPlugin))
        ).pipe(map(list => list.filter((x): x is ActionItem => x !== null)));
    }

    private getPluginBulkAction(record: ApiFragment, plugin: MediaHavenPlugin): Observable<ActionItem | null> {
        const actionService: ShPluginActionProvider = this.PLUGIN_SERVICE_MAPPING[plugin];

        return this.checkPluginActive(plugin).pipe(
            switchMap(isActive => (isActive ? actionService.getBulkAction(record, plugin) : of(null)))
        );
    }

    public getPluginBatchActions(): Observable<ActionItem[]> {
        return combineLatest(
            Object.keys(MediaHavenPlugin).map(plugin => this.getPluginBatchAction(plugin as MediaHavenPlugin))
        ).pipe(map(list => list.filter((x): x is ActionItem => x !== null)));
    }

    private getPluginBatchAction(plugin: MediaHavenPlugin): Observable<ActionItem | null> {
        const actionService: ShPluginActionProvider = this.PLUGIN_SERVICE_MAPPING[plugin];

        return this.checkPluginActive(plugin).pipe(
            switchMap(isActive => (isActive ? actionService.getBatchAction(plugin) : of(null)))
        );
    }

    private checkPluginActive(plugin: MediaHavenPlugin): Observable<boolean> {
        return this.pluginGuard.checkPlugins([plugin]);
    }
}
